import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ALL_DAYS, SHORT_DAY_NAMES, encodeDaysOfWeek } from '@/lib/cron/dayOfWeek';
import type { SchedulePreset } from './cronExpressionToPreset';

export type FormState =
  | null
  | { frequency: 'off' }
  | { frequency: 'hourly'; minute: number }
  | { frequency: 'daily';   hour: number; minute: number; ampm: 'AM' | 'PM'; days: number[] }
  | { frequency: 'weekly';  hour: number; minute: number; ampm: 'AM' | 'PM'; day: number }
  | { frequency: 'monthly'; hour: number; minute: number; ampm: 'AM' | 'PM'; day: number };

const FREQUENCIES: ReadonlyArray<['off' | 'hourly' | 'daily' | 'weekly' | 'monthly', string]> = [
  ['off', 'Off'], ['hourly', 'Hourly'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly'],
];

const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const DOW = [['0', 'Sunday'], ['1', 'Monday'], ['2', 'Tuesday'], ['3', 'Wednesday'], ['4', 'Thursday'], ['5', 'Friday'], ['6', 'Saturday']] as const;
const DOM = Array.from({ length: 28 }, (_, i) => i + 1);

function defaultForFrequency(f: 'off' | 'hourly' | 'daily' | 'weekly' | 'monthly'): FormState {
  switch (f) {
    case 'off':     return { frequency: 'off' };
    case 'hourly':  return { frequency: 'hourly',  minute: 0 };
    case 'daily':   return { frequency: 'daily',   hour: 2, minute: 0, ampm: 'AM', days: [...ALL_DAYS] };
    case 'weekly':  return { frequency: 'weekly',  hour: 2, minute: 0, ampm: 'AM', day: 1 };
    case 'monthly': return { frequency: 'monthly', hour: 2, minute: 0, ampm: 'AM', day: 1 };
  }
}

interface Props {
  value: FormState;
  initial?: FormState;
  onChange: (next: FormState) => void;
  onSave?: (out: { cron: string | null; enabled: boolean }) => void;
  isSaving?: boolean;
}

export function ScheduleFrequencyForm({ value, initial, onChange, onSave, isSaving }: Props) {
  const current = value?.frequency;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FREQUENCIES.map(([f, label]) => (
          <Button
            key={f}
            type="button"
            aria-pressed={current === f}
            variant={current === f ? 'default' : 'outline'}
            onClick={() => onChange(defaultForFrequency(f))}
          >
            {label}
          </Button>
        ))}
      </div>

      {value?.frequency === 'hourly' && (
        <Field label="Minute">
          <NumberSelect ariaLabel="Minute" value={value.minute} options={MINUTES}
                        onChange={(n) => onChange({ ...value, minute: n })} formatter={(n) => String(n).padStart(2, '0')} />
        </Field>
      )}

      {(value?.frequency === 'daily' || value?.frequency === 'weekly' || value?.frequency === 'monthly') && (
        <>
          {value.frequency === 'daily' && (
            <Field label="Days">
              <WeekdayPicker days={value.days} onChange={(days) => onChange({ ...value, days })} />
            </Field>
          )}
          {value.frequency === 'weekly' && (
            <Field label="Day of week">
              <Select value={String(value.day)} onValueChange={(v) => onChange({ ...value, day: Number(v) })}>
                <SelectTrigger aria-label="Day of week"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOW.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {value.frequency === 'monthly' && (
            <Field label="Day of month">
              <NumberSelect ariaLabel="Day of month" value={value.day} options={DOM}
                            onChange={(n) => onChange({ ...value, day: n })} />
            </Field>
          )}
          <Field label="Time">
            <div className="flex items-center gap-2">
              <NumberSelect ariaLabel="Hour" value={value.hour} options={HOURS_12}
                            onChange={(n) => onChange({ ...value, hour: n })} />
              <span>:</span>
              <NumberSelect ariaLabel="Minute" value={value.minute} options={MINUTES}
                            onChange={(n) => onChange({ ...value, minute: n })} formatter={(n) => String(n).padStart(2, '0')} />
              <Select value={value.ampm} onValueChange={(v) => onChange({ ...value, ampm: v as 'AM' | 'PM' })}>
                <SelectTrigger aria-label="AM/PM"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AM">AM</SelectItem>
                  <SelectItem value="PM">PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Field>
        </>
      )}

      {onSave && (
        <div className="pt-2">
          <Button
            type="button"
            onClick={() => onSave({ cron: formStateToCron(value), enabled: value?.frequency !== 'off' })}
            disabled={JSON.stringify(value) === JSON.stringify(initial) || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-2">
      <Label>{label}</Label>
      <div>{children}</div>
    </div>
  );
}

// #2191: Daily runs on any non-empty subset of weekdays. The last ticked day
// is disabled rather than allowing an empty set, which has no cron form.
const DAY_FULL_NAMES = DOW.map(([, label]) => label);

function WeekdayPicker({ days, onChange }: { days: number[]; onChange: (days: number[]) => void }) {
  const toggle = (d: number, checked: boolean) =>
    onChange(checked ? [...days, d].sort((a, b) => a - b) : days.filter((x) => x !== d));
  return (
    <div className="space-y-1">
      <div role="group" aria-label="Days" className="flex flex-wrap gap-3">
        {ALL_DAYS.map((d) => {
          const checked = days.includes(d);
          return (
            <label key={d} className="flex items-center gap-1.5 text-sm">
              <Checkbox
                aria-label={DAY_FULL_NAMES[d]}
                checked={checked}
                disabled={checked && days.length === 1}
                onCheckedChange={(c) => toggle(d, c === true)}
              />
              {SHORT_DAY_NAMES[d]}
            </label>
          );
        })}
      </div>
      {days.length === 1 && (
        <p className="text-xs text-muted-foreground">One day is the same schedule as Weekly, and is shown as Weekly once saved.</p>
      )}
    </div>
  );
}

function NumberSelect({ ariaLabel, value, options, onChange, formatter }: {
  ariaLabel: string; value: number; options: number[]; onChange: (n: number) => void; formatter?: (n: number) => string;
}) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger aria-label={ariaLabel} className="w-[100px]"><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((n) => <SelectItem key={n} value={String(n)}>{formatter ? formatter(n) : String(n)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function to24Hour(hour12: number, ampm: 'AM' | 'PM'): number {
  if (hour12 === 12) return ampm === 'AM' ? 0 : 12;
  return ampm === 'AM' ? hour12 : hour12 + 12;
}

export function formStateToCron(s: FormState): string | null {
  if (!s || s.frequency === 'off') return null;
  if (s.frequency === 'hourly')  return `${s.minute} * * * *`;
  const h = to24Hour(s.hour, s.ampm);
  if (s.frequency === 'daily')   return s.days.length ? `${s.minute} ${h} * * ${encodeDaysOfWeek(s.days)}` : null;
  if (s.frequency === 'weekly')  return `${s.minute} ${h} * * ${s.day}`;
  if (s.frequency === 'monthly') return `${s.minute} ${h} ${s.day} * *`;
  return null;
}

export function presetToFormState(p: SchedulePreset | null): FormState {
  if (!p) return null;
  return p as unknown as FormState;
}
